/**
 * Role approvals — the shapes the proxy sends.
 *
 * Mirrors `proxy-server/lib/roleApprovals.js` and `roleApprovalsStore.js`. The three approver keys
 * are role names rather than people's names on purpose: the office outlives the officeholder, and
 * the Airtable columns are keyed the same way.
 */
import type { RoleCompensation } from './compensation';
import type { Rating } from './rolePrioritisation';

export type ApproverKey = 'cpo' | 'ceo' | 'cfo';

/** What one approver has said. `null` means they have not decided, which is NOT the same as no. */
export type ApprovalDecision = 'Approved' | 'Declined' | null;

/** The lifecycle of a proposal, derived by the proxy from the three decisions below — never set
 * independently, so these two can never contradict each other. */
export type ProposalStatus = 'Proposed' | 'Approved' | 'Declined' | 'Cancelled';

/** The TA Cockpit's Role Prioritisation vocabulary, highest first — the SAME type, not a copy. */
export type PriorityRating = Rating;
export const PRIORITY_RATINGS: PriorityRating[] = ['AAA', 'A', 'B', 'C'];

/**
 * A proposal's two ratings — mirrors `proxy-server/lib/roleApprovalPriority.js`. `suggested` is the
 * proposer's (from the form), `approved` is the CEO's and the only one Role Prioritisation counts.
 */
export interface ProposalPriority {
  suggested: PriorityRating | null;
  approved: PriorityRating | null;
  /** Set when a former CEO (Maxi) set the approved rating: shown beside it, and nobody may change
   * it — the proxy refuses. Null otherwise. */
  approvedByName?: string | null;
}

export interface ApproverState {
  key: ApproverKey;
  /** "Chief People Officer", "CEO", "CFO". */
  title: string;
  name: string;
  email: string;
  decision: ApprovalDecision;
  decidedAt: string | null;
  /** Set when a former holder of the office made this decision (Maxi as CEO, before Patrick): the
   * card shows their name on it, and nobody may change it — the proxy refuses. Null otherwise. */
  decidedByName?: string | null;
}

export interface RoleProposal {
  id: string;
  title: string;
  role: string | null;
  /** The Airtable column is `People Team`; the form and this page call it the reporting line. */
  peopleTeam: string | null;
  unit: string | null;
  trackAndLevel: string | null;
  costCategory: string | null;
  reasonForHiring: string | null;
  hiringManager: string | null;
  status: ProposalStatus | null;
  plannedStartDate: string | null;
  createdOn: string | null;
  rationale: string | null;
  proposedBy: string | null;
  approvals: ApproverState[];
  /** Optional: an older proxy revision does not send it. */
  priority?: ProposalPriority;
  /**
   * The role's salary band and what it costs this year, attached by the proxy when the queue is
   * read (`roleApprovalsStore.fetchApprovalQueue`) rather than fetched by the card.
   *
   * ⚠️ Never null in practice, but typed as nullable because a salary-bands outage must not blank
   * the approval queue — the row then carries `unavailable: 'bands_unavailable'` instead.
   */
  compensation: RoleCompensation | null;
  /**
   * Set once the role has been copied into `hiring_plan`. Its presence means the approvals are
   * history: the proposal is locked, and the Org Planning Overview reads the role from the hiring
   * plan instead of from here.
   */
  hiringPlanRecordId: string | null;
  /**
   * Shared by every role of one bulk submission; the page shows those roles as one card. Null on a
   * single proposal. Optional: an older proxy revision does not send it.
   */
  submissionId?: string | null;
  approvalLog: string | null;
}

export interface ApprovalViewer {
  email: string;
  /**
   * Every office this caller may sign for; empty for everyone else. More than one for a delegate on
   * several offices (David signs for the CPO and the CEO).
   */
  approverKeys: ApproverKey[];
  /**
   * The offices among those the caller holds as a DELEGATE rather than as the officeholder — see
   * the `APPROVERS` warning in `proxy-server/lib/roleApprovals.js`. Resolved server-side and never
   * inferred here: the officeholder's name is deliberately what every viewer sees, so "the name is
   * not mine" would mark everyone a delegate.
   */
  delegateKeys: ApproverKey[];
  /** A People Space admin may record a decision on an approver's behalf; the log says who did. */
  isAdmin: boolean;
  /**
   * May approve or change a proposal's priority rating — the CEO officeholder and nobody else, not
   * a delegate and not an admin. Resolved by the proxy, which re-checks it on every write.
   */
  canSetPriority?: boolean;
}

/**
 * A change to a COMMITTED hiring-plan role that raises its cost by more than 10% — held, and
 * written to the Hiring Database only once all three approve. Mirrors
 * `proxy-server/lib/roleChangeRequests.js`, whose module note lists what each state means.
 */
export type ChangeRequestState =
  | 'pending'
  | 'approved'
  | 'declined'
  | 'applied'
  | 'conflict'
  | 'withdrawn'
  | 'superseded';

export interface RoleChangeRequest {
  id: string;
  recordId: string;
  title: string;
  /** "Track and Level: IC3 → IC4", one per changed field. */
  descriptions: string[];
  cost: { before: RoleCompensation | null; after: RoleCompensation | null } | null;
  reason: 'cost_increase' | 'uncostable';
  requestedBy: string;
  createdAt: string;
  state: ChangeRequestState;
  approvals: ApproverState[];
  log: string[];
  appliedAt: string | null;
  /** Approved but not written: the retry reason, or — in `conflict` — why it never will be. */
  applyError: string | null;
}

export interface RoleApprovalsResponse {
  proposals: RoleProposal[];
  /** Optional: an older proxy revision does not send it. */
  changeRequests?: RoleChangeRequest[];
  changeRequestsError?: string | null;
  viewer: ApprovalViewer;
  /** Who owns the priority rating, by name — for "awaiting Patrick Schmidt" on the card. */
  priorityOwner?: string | null;
  approvers: { key: ApproverKey; title: string; name: string }[];
  generatedAt: string;
}

/** What happened to the hiring-plan copy a third approval triggers. */
export interface TransferOutcome {
  ok: boolean;
  hiringPlanRecordId?: string;
  skipped?: string;
  /** True only when the role EXISTS in the hiring plan but the proposal could not be stamped —
   * the one failure that needs a person rather than a retry, because it double-counts the role. */
  needsManualFix?: boolean;
  message?: string;
  /** The role IS in the hiring plan, but without its approved rating — set it on the HDB row. */
  priorityDropped?: boolean;
}

export interface DecisionResponse {
  ok: boolean;
  /** Null when the verdict was recorded but its status could not be recomputed — see `warning`. */
  status: ProposalStatus | null;
  transfer: TransferOutcome | null;
  /**
   * The decision landed but the row's derived status did not. Not an error: the verdict is in its
   * own column, and the next decision recomputes the status from all three. Shown all the same,
   * because a card that disagrees with itself is worse than one that says why.
   */
  statusStale?: boolean;
  warning?: string;
  /** Set when the CEO's approval also changed the approved rating; `notice` is the Slack outcome. */
  priority?: {
    approved: PriorityRating | null;
    notice: { notified: boolean; reason?: string } | null;
    /** The approval was recorded but Airtable refused the rating that rode with it. */
    failed?: boolean;
  } | null;
}

/** One role's outcome inside a bulk decision — `DecisionResponse` plus which role, or the error. */
export interface SubmissionDecisionResult extends Partial<DecisionResponse> {
  id: string;
  ok: boolean;
  error?: string;
}

/**
 * "Approve all" / "Decline all" on a bulk submission. Only the roles still waiting on that approver
 * are decided; the rest come back in `skipped` with the reason (`already_decided`, or the refusal a
 * single decision would have given).
 */
export interface SubmissionDecisionResponse {
  ok: boolean;
  results: SubmissionDecisionResult[];
  skipped: { id: string; reason: string; message?: string }[];
}
